import { GridOptions, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, ViewEncapsulation } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { Addon, AddonBin, AddonBinStatusEnum, AddonStation, ItemDetail, ItemDetailAddon } from 'app/model/entities/entity-model';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { BarcodeService } from 'app/shared/barcode.service';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { DomainBaseComponent, wrapBusy } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { FileViewDialogComponent, FileViewDialogParams, IDoc } from 'app/shared/file-view-dialog.component';
import { NavFns } from 'app/shared/nav-fns';
import { UnitOfWork } from 'app/shared/unit-of-work';
import _ from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { AddonBinSplitDialogComponent } from './addon-bin-split-dialog.component';
import { AddonBinSetPriorityDialogComponent } from './addon-bin-set-priority-dialog.component';
import { UniAgFns } from '@/shared/uni-ag-fns';

@Component({
  selector: 'app-addon-bin',
  templateUrl: './addon-bin.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddonBinComponent extends DomainBaseComponent {
  shouldShowClosedBins!: boolean;
  addonStations!: AddonStation[];
  selectedAddonStationId: number = 0;

  addonBins!: AddonBin[];
  addonBinGridOptions!: GridOptionsExt;
  selectedAddonBin!: AddonBin;

  addons!: Addon[];
  addonGridOptions!: GridOptionsExt;

  itemDetailGridOptions!: GridOptionsExt;
  itemDetails!: ItemDetail[];

  interval: any;
  refreshOptions!: any[];
  refreshSeconds = 0;
  showRefreshIcon = false;
  selectedBinCount = 0;
  private readonly _selected = '_selected';

  constructor(
    public uow: UnitOfWork,
    public barcodeService: BarcodeService,
    protected domainService: DomainService,
    protected route: ActivatedRoute,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async setupRefresh() {
    // DON"T UpdateLocation because it will lose the current selection.
    // this.updateLocation();
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.refreshSeconds == 0) return;
    this.interval = setInterval(async () => {
      if (this.isPageReady) {
        this.showRefreshIcon = true;
        await this.refreshGrids();
        this.showRefreshIcon = false;
      }
    }, this.refreshSeconds * 1000);
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.uow.clearEntities(AddonBin);
    this.uow.clearEntities(ItemDetailAddon);

    return true;
  }

  async updateFromContext() {
    // Now available from this.captureLocation.
    // const qparams = this.route.snapshot.queryParams;

    this.refreshOptions = [
      { name: 'Every 10 Seconds', refreshSeconds: 10 },
      { name: 'Every 30 Seconds', refreshSeconds: 30 },
      { name: 'Every 60 Seconds', refreshSeconds: 60 },
      { name: 'Every 90 Seconds', refreshSeconds: 90 },
      { name: 'Every 180 Seconds', refreshSeconds: 180 },
    ];

    this.addonBinGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAddonBinGridReady,
      onRowSelected: this.onAddonBinRowSelected,
      onFilterChanged: this.onFilterChanged,
      onSortChanged: this.onFilterChanged,
      onFirstDataRendered: (e) => {
        this.applyLocation();
        this.AgFns.sizeColumnsToFit(this.addonBinGridOptions);
      },
    });

    const qparams = this.captureLocation(this.addonBinGridOptions, 'id');

    this.refreshSeconds = +(qparams['refresh'] ?? 90) || 90;
    this.setupRefresh();
    this.selectedAddonStationId = +(qparams['stationId'] ?? 0);
    this.shouldShowClosedBins = !!+(qparams['showClosed'] ?? 0);

    this.addonGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAddonGridReady,
    });

    this.itemDetailGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onItemDetailGridReady,
        onFirstDataRendered: (e) => {
          this.AgFns.sizeColumnsToFit(this.itemDetailGridOptions);
        },
      },
      { detailProperty: 'itemDetailAddons' },
    );
    this.updateItdMasterDetail(this.itemDetailGridOptions);

    this.addonStations = await this.dbQueryService.getAll(AddonStation);
    this.addonStations.unshift({ id: 0, name: '[ All ]' } as AddonStation);

    this.isPageReady = true;

    await this.refreshGrids();
  }

  async onAddonBinGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createCheckboxProps('', this._selected, 'selectBin'),
        editable: (p: any) => p.data.addonBinStatusId !== AddonBinStatusEnum.Closed,
        valueGetter: (p: any) => !!p.data[this._selected],
        valueSetter: (p: any) => {
          p.data[this._selected] = p.newValue;
          this.updateSelectedBinCount();
          return true;
        },
        cellStyle: (p: any) => (p.data.addonBinStatusId === AddonBinStatusEnum.Closed ? { opacity: 0.3, pointerEvents: 'none' } : null),
        ...AgFns.fixWidth(80),
      },
      {
        headerName: 'Bin Id',
        field: 'id',
        filter: 'agNumberColumnFilter',
        cellClass: 'ag-left-align-cell',
        headerClass: 'ag-left-align-cell',
        ...AgFns.fixWidth(120),
      },
      {
        ...AgFns.createButtonProps('', this.onProcessBin.bind(this), {
          label: 'Process',
        }),
        ...AgFns.fixWidth(80),
      },
      {
        ...AgFns.createButtonProps('', this.onSplitBin.bind(this), {
          label: 'Split',
          canDisplay: this.canSplitOrCombineBin.bind(this),
        }),
        ...AgFns.fixWidth(80),
      },

      {
        headerName: 'Bin Status',
        field: 'addonBinStatus.name',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Account',
        field: 'account.accountName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Priority Order',
        field: 'priorityOrder',
        filter: 'agNumberColumnFilter',
        editable: this.isColumnEditable,
        cellClass: (p: any) => (this.isColumnEditable(p) ? 'cell-editable' : ''),
        type: 'numericColumn',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Pending Station',
        field: 'pendingAddonStation.name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Current Station',
        field: 'currentAddonStation.name',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Current Station Status',
        field: 'currentAddonBinStationStatus.name',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(200),
      },
      {
        headerName: 'Single Job Order',
        field: 'joHeaderId',
        filter: 'agNumberColumnFilter',
        ...AgFns.fixWidth(180),
      },
      {
        ...AgFns.createButtonProps('', this.onPrintBinLabel.bind(this), { label: 'Reprint' }),
        ...AgFns.fixWidth(120),
      },
      // { headerName: "Grouping Key", field: "aggregateGroupingKey" },
    ];

    const sortModel = [
      { colId: 'priorityOrder', sort: 'asc' as const },
      { colId: 'account.accountName', sort: 'desc' as const },
    ];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  isColumnEditable(p: any) {
    return p.data.addonBinStatusId != AddonBinStatusEnum.Closed;
  }

  onAddonGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Addon', field: 'nameAndLocation', ...AgFns.flexWidth(200) },
      { headerName: 'Station', field: 'addonStation.name', ...AgFns.flexWidth(100) },
      { headerName: 'Instructions', field: 'instructions', ...AgFns.flexWidth(200) },
      {
        ...AgFns.createButtonProps('', this.onViewAddonImages.bind(this), {
          label: 'Images',
          calcClass: (row) => (this.hasAddonImages(row) ? 'btn-has-data' : ''),
        }),
        ...AgFns.fixWidth(100),
      },
      {
        ...AgFns.createButtonProps('', this.onViewAddonDocs.bind(this), {
          label: 'Docs',
          calcClass: (row) => (this.hasAddonDocs(row) ? 'btn-has-data' : ''),
        }),
        ...AgFns.fixWidth(100),
      },
    ];

    const sortModel = [
      { colId: 'addonStation.name', sort: 'asc' as const },
      { colId: 'nameAndLocation', sort: 'asc' as const },
    ];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  onItemDetailGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Inventory Identifier',
        field: 'id',
        cellRenderer: 'agGroupCellRenderer',
        cellClass: 'ag-left-aligned-cell',
        ...AgFns.fixWidth(160),
      },
      { ...UniAgFns.getManufacturerColDef('product.productType.manufacturer.name') },
      { ...UniAgFns.getProductTypeSKUColDef('product.productType.style') },
      { ...UniAgFns.getProductTypeDescriptionColDef('product.productType.description') },
      { ...UniAgFns.getFeatureChoicesColDef('product.featureChoicesExtract') },

      {
        ...AgFns.createButtonProps('', this.onPrintItdBarCode.bind(this), {
          label: 'Reprint',
        }),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Job Order',
        ...NavFns.createIdCellClickedNavProps('joDetail.joHeaderId', this.router, '/in-process/manage/job-orders'),
        ...AgFns.fixWidth(120),
      },
      // { headerName: "Job Order Detail", field: "joDetail.id" },
    ];

    const sortModel = [{ colId: 'itemDetail.id', sort: 'asc' as const }];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  updateItdMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: 'Addon', field: 'addon.nameAndLocation' },
      { headerName: 'Station', field: 'addon.addonStation.name' },
      { headerName: 'Instructions', field: 'addon.instructions' },
      { headerName: 'Addl. Info', field: 'additionalInfo' },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params: any) => {
        const itd = params.data as ItemDetail;
        var itdAddons = _.sortBy(
          itd.itemDetailAddons,
          (itda) => itda.addon.addonStation.name,
          (itda) => itda.addon.nameAndLocation,
        );
        if (this.selectedAddonStationId != 0) {
          itdAddons = itdAddons.filter((x) => x.addon.addonStationId == this.selectedAddonStationId);
        }
        params.successCallback(itdAddons);
      },
    };
  }

  @wrapBusy
  async onAddonBinRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) {
      return;
    }

    const addonBin = e.data as AddonBin;
    if (!addonBin || this.selectedAddonBin === addonBin) {
      return;
    }

    // check if a deselect event and ignore

    this.selectedAddonBin = addonBin;
    this.itemDetails = await this.dbQueryService.getItemDetailsForAddonBin(addonBin.id);
    const addons = _.flatMap(this.itemDetails, (x) => x.itemDetailAddons.map((y) => y.addon));
    this.addons = Array.from(new Set(addons));
    await this.AgFns.refreshGrid(this.addonGridOptions, this.addons);
    await this.AgFns.refreshGrid(this.itemDetailGridOptions, this.itemDetails);

    this.replaceLocation(this.selectedAddonBin?.id);
  }

  async onSplitBin(addonBin: AddonBin) {
    const itemDetails = await this.dbQueryService.getItemDetailsForAddonBin(addonBin.id);
    if (itemDetails.length <= 1) {
      this.toastr.warning('This bin only has a single item and cannot be split.');
      return;
    }
    const r = await DialogBaseComponent.open(AddonBinSplitDialogComponent, this, { addonBin: addonBin });
    if (r) {
      await this.refreshGrids(addonBin);
    }
  }

  public async refreshAndUpdate(shouldUpdateLocation = true) {
    if (shouldUpdateLocation) {
      this.replaceLocation(this.selectedAddonBin?.id);
    }
    await this.refreshGrids();
  }

  @wrapBusy
  public async refreshGrids(addonBin?: AddonBin) {
    addonBin = addonBin ?? this.selectedAddonBin;
    this.addonBins = await this.dbQueryService.getAddonBins(this.shouldShowClosedBins);
    if (this.selectedAddonStationId != 0) {
      this.addonBins = this.addonBins.filter(
        (aob) => aob.pendingAddonStationId == this.selectedAddonStationId || aob.currentAddonStationId == this.selectedAddonStationId,
      );
    }
    await AgFns.refreshGrid(this.addonBinGridOptions, this.addonBins);
    if (this.addonBins.includes(addonBin)) {
      this.itemDetails = await this.dbQueryService.getItemDetailsForAddonBin(addonBin.id);
    } else {
      this.selectedAddonBin = null;
      this.itemDetails = [];
    }
    const addons = _.flatMap(this.itemDetails, (x) => x.itemDetailAddons.map((y) => y.addon));
    this.addons = Array.from(new Set(addons));
    AgFns.refreshGrid(this.addonGridOptions, this.addons);
    AgFns.refreshGrid(this.itemDetailGridOptions, this.itemDetails);

    // AgFns.selectDefaultRow(this.addonBinGridOptions);
  }

  canSplitOrCombineBin(addonBin: AddonBin) {
    return addonBin.addonBinStatusId <= AddonBinStatusEnum.Open;
  }

  @wrapBusy
  async onPrintBinLabel(addonBin: AddonBin) {
    this.itemDetails = await this.dbQueryService.getItemDetailsForAddonBin(addonBin.id);
    let addons = _.flatMap(this.itemDetails, (x) => x.itemDetailAddons.map((y) => y.addon));
    addons = Array.from(new Set(addons));
    let grps: { addonBin: AddonBin; addons: Addon[] }[] = [];
    grps.push({ addonBin, addons });
    await this.barcodeService.printAddonBinLabels(grps, true);
  }

  onProcessBin(addonBin: AddonBin) {
    this.router.navigate(['./in-process/manage/addon-bin', addonBin.id]);
  }

  onFilterChanged() {
    this.replaceLocation();
  }

  replaceLocation(key: any = null) {
    const otherQueryParams = {
      refresh: this.refreshSeconds,
      stationId: this.selectedAddonStationId,
      showClosed: this.shouldShowClosedBins ? 1 : 0,
    };
    super.replaceLocation(key, otherQueryParams);
  }

  hasAddonDocs(addon: Addon) {
    return addon.addonDocMaps.length > 0;
  }

  async onViewAddonDocs(addon: Addon) {
    const data = {
      title: 'View Addon Documents',
      docs: addon.addonDocMaps.map((x) => x.addonDoc as IDoc),
    } as const;
    if (data.docs.length == 0) {
      this.toastr.warning('No documents for this Add-on');
      return;
    }
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
  }

  hasAddonImages(addon: Addon) {
    return addon.addonDocMaps.length > 0;
  }

  async onViewAddonImages(addon: Addon) {
    const data: FileViewDialogParams = {
      header: 'View Addon Images',
      docs: addon.addonImages.map((x) => x.image as IDoc),
    };
    if (data.docs!.length == 0) {
      this.toastr.warning('No images for this Add-on');
      return;
    }
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
  }

  @wrapBusy
  async onPrintItdBarCode(itd: ItemDetail) {
    return await this.barcodeService.printJobOrderItemDetailBarCode(itd, true);
  }

  updateSelectedBinCount() {
    this.selectedBinCount = (this.addonBins || []).filter((b) => b[this._selected]).length;
  }

  @wrapBusy
  async onSetPriority() {
    const selected = (this.addonBins || []).filter((b) => b[this._selected]);
    if (selected.length === 0) return;

    const result = await DialogBaseComponent.open(AddonBinSetPriorityDialogComponent, this, {
      binCount: selected.length,
    });
    if (result) {
      const priority = +result;
      selected.forEach((b) => (b.priorityOrder = priority));
      await this.dbSaveService.saveChanges();
      selected.forEach((b) => (b[this._selected] = false));
      this.updateSelectedBinCount();
      AgFns.refreshGrid(this.addonBinGridOptions, this.addonBins);
      this.toastr.success(`Priority set to ${priority} for ${selected.length} bin${selected.length !== 1 ? 's' : ''}`);
    }
  }

  async cancelChanges() {
    this.addonBinGridOptions.apiX!.stopEditing();
    this.uow.rollback();
    this.addonBinGridOptions.apiX!.redrawRows();
  }

  async saveChanges() {
    this.addonBinGridOptions.apiX!.stopEditing();
    const sr = await this.dbSaveService.saveChanges();
    AgFns.refreshGrid(this.addonBinGridOptions, this.addonBins);
    this.toastr.success('Add-On Bin saved');
  }

  // Needs work if we revisit it later.
  // async onCombineBin(addonBin: AddonBin) {
  //   const aobs = this.addonBins.filter(x =>
  //     x.accountId == addonBin.accountId
  //     && x.aggregateGroupingKey == addonBin.aggregateGroupingKey
  //     && x.joHeaderId == null && x.id != addonBin.id);
  //   if (aobs.length == 0) {
  //     this.dialogWrapperService.showOkMessage('Nothing to combine', 'There are no Addon Bins that can be combined with this one.')
  //     return;
  //   }

  //   for (const aob of aobs) {
  //     const itemDetails = await this.dbQueryService.getItemDetailsForBin(aob.id)
  //     // this works because of line above
  //     const itdas = _.flatMap(itemDetails, itd => itd.itemDetailAddons);
  //     itdas.forEach(x => x.addonBinId = addonBin.id);
  //   }
  //   await this.dbSaveService.saveChanges();

  //   aobs.forEach(aob => {
  //     aob.entityAspect.setDeleted();
  //   });
  //   await this.dbSaveService.saveChanges();
  //   this.refreshGrids(addonBin);
  //   this.dialogWrapperService.showOkMessage('AddonBins were combined', `There were ${aobs.length} Addon Bins that were combined into this one`);
  // }
}
